import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../../../shared/components/SEO/SEO';
import { useAuth } from '../../../../contexts/AuthContext';
import { forumAPI, type ForumCategory } from '../../../../services/api';
import RichTextEditor from '../../../../shared/components/RichTextEditor/RichTextEditor';
import { stripHtml } from './utils';
import './Community.css';

const MAX_ATTACHMENTS = 1;
const MAX_FILE_SIZE_MB = 5;
const IMAGE_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

const CreatePost: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [categoryId, setCategoryId] = useState<number | ''>('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?next=' + encodeURIComponent('/community/new'), { replace: true });
      return;
    }
    forumAPI.getCategories()
      .then((res) => {
        const list = res.data as ForumCategory[];
        setCategories(list);
        if (list.length > 0 && !categoryId) setCategoryId(list[0].id);
      })
      .catch(() => setCategories([]));
  }, [isAuthenticated, authLoading, navigate]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const chosen = Array.from(e.target.files || []);
    const maxSize = MAX_FILE_SIZE_MB * 1024 * 1024;
    const valid = chosen.filter((f) => f.size <= maxSize && f.type.startsWith('image/'));
    setFiles(valid.slice(0, 1));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedBody = body.replace(/<p><br><\/p>/g, '').replace(/<p><\/p>/g, '').trim();
    const hasContent = trimmedBody && stripHtml(trimmedBody).trim();
    if (!categoryId || !title.trim() || !hasContent) {
      setError('Please fill in all required fields: Category, Title, Content, and at least one image.');
      return;
    }
    if (files.length === 0) {
      setError('Please add at least one image. Image is required.');
      return;
    }
    setSubmitting(true);
    setError('');
    const payload = { category: Number(categoryId), title: title.trim(), body: trimmedBody };
    forumAPI.createPost(payload, files)
      .then(() => {
        navigate('/community?pending=1', { replace: true });
      })
      .catch((err) => {
        const data = err.response?.data;
        const msg = data?.attachments?.[0] || data?.title?.[0] || data?.body?.[0] || data?.detail || 'Failed to create post.';
        setError(typeof msg === 'string' ? msg : 'Failed to create post.');
      })
      .finally(() => setSubmitting(false));
  };

  if (authLoading || !isAuthenticated) {
    return (
      <div className="community-page">
        <div className="community-empty">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <SEO title="New post - Crypxa Community" description="Create a new discussion post." />
      <div className="community-page community-form-page">
        <div className="community-header">
          <h1>New post</h1>
          <Link to="/community" className="community-btn secondary">Cancel</Link>
        </div>
        <form className="community-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="category">Category <span className="form-required">*</span></label>
            <select
              id="category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value ? Number(e.target.value) : '')}
              required
            >
              <option value="">Select category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="title">Title <span className="form-required">*</span></label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Post title"
              maxLength={255}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="body">Content <span className="form-required">*</span></label>
            <p className="form-hint" style={{ marginBottom: '0.5rem' }}>Use the toolbar for bold, italic, headings, lists and more.</p>
            <RichTextEditor
              id="body"
              value={body}
              onChange={setBody}
              placeholder="Write your post..."
            />
          </div>
          <div className="form-group form-group-attachments">
            <label>Images <span className="form-required">*</span></label>
            <p className="form-hint">One image required. Max {MAX_FILE_SIZE_MB}MB. JPEG, PNG, GIF, WebP.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept={IMAGE_ACCEPT}
              onChange={onFileChange}
              className="community-file-input-hidden"
              aria-label="Choose images"
            />
            <div
              role="button"
              tabIndex={0}
              className="community-attach-zone community-attach-zone-images"
              onClick={() => files.length === 0 && fileInputRef.current?.click()}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && files.length === 0) fileInputRef.current?.click(); }}
              aria-label="Add image"
            >
              <span className="community-attach-icon">🖼</span>
              <span>{files.length ? 'Image added (click to replace)' : 'Click to add image'}</span>
            </div>
            {files.length > 0 && (
              <div className="community-image-previews">
                {files.map((f, i) => (
                  <div key={i} className="community-image-preview-wrap">
                    <img src={URL.createObjectURL(f)} alt="" className="community-image-preview" />
                    <button type="button" onClick={() => removeFile(i)} className="community-attachment-remove community-image-preview-remove" aria-label="Remove">×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {error && <p style={{ color: 'var(--color-Error)', fontSize: '0.9rem', marginBottom: '0.75rem' }}>{error}</p>}
          <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
            Your post will be reviewed by a moderator before it appears on the forum.
          </p>
          <div className="form-actions">
            <button type="submit" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create post'}
            </button>
            <Link to="/community">Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreatePost;
